import { capitalize } from 'lodash'
import moment from 'moment'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useWindowResize } from '../../hooks/useWindowResize'
import { Bookable, BookablesByDate, sortBookablesByStartTime } from '../../models/Bookable'
import { CalendarFilter } from '../../models/CalendarFilter'
import { Payee } from '../../models/Payee'
import { User } from '../../models/User'
import { MONTH_HEADER_HEIGHT, getCalendarMonthByDate } from '../../utils/calendar'
import { DATE_KEY_FORMAT, DAYS_OF_WEEK, isDayInMonth, isToday } from '../../utils/dates'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { CalendarDayBookables } from './CalendarDayBookables'

type Props = {
    heightOffset: number
    currentDate: moment.Moment
    pros: User[]
    filter: CalendarFilter
    bookablesByDate: BookablesByDate
    showBanner: boolean
    onGoToDay: (day: moment.Moment) => void
}

export const CalendarMonth = ({
    heightOffset,
    currentDate,
    pros,
    filter,
    bookablesByDate,
    showBanner,
    onGoToDay,
}: Props) => {
    const calendarBodyRef = useRef<HTMLDivElement>(null)

    const { height } = useWindowResize()

    const [maxBookablesPerDay, setMaxBookablesPerDay] = useState(2)

    const payees = useMemo(() => pros.map((u) => u.subtype) as Payee[], [pros])
    const weeksToRender = useMemo(() => getCalendarMonthByDate(currentDate), [currentDate])

    useLayoutEffect(() => {
        if (!calendarBodyRef.current) return
        const PADDING_OFFSET = 32
        const BOOKABLE_HEIGHT = 30
        const availableHeight =
            calendarBodyRef.current.clientHeight / weeksToRender.length - PADDING_OFFSET
        const maxBookables = Math.floor(availableHeight / BOOKABLE_HEIGHT)
        setMaxBookablesPerDay(maxBookables > 0 ? maxBookables : 1)
    }, [height, filter.isEmpty, showBanner, weeksToRender.length])

    const getBookables = useCallback(
        (day: moment.Moment): Bookable[] =>
            (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? []).sort(sortBookablesByStartTime),
        [bookablesByDate]
    )

    return (
        <Card
            className="mt-6 mb-8"
            style={{
                height: `calc(100% - ${heightOffset}px)`,
                maxWidth: '100%',
                minWidth: '1024px',
            }}
        >
            <div
                className="flex items-center rounded-t-lg border-[1px] border-gray-300 bg-gray-100"
                style={{ height: `${MONTH_HEADER_HEIGHT}px` }}
            >
                {DAYS_OF_WEEK.map((d) => (
                    <Paragraph
                        size="sm"
                        key={d}
                        className="w-[calc(100%/7)] text-center text-gray-600"
                    >
                        {capitalize(d)}
                    </Paragraph>
                ))}
            </div>
            <div ref={calendarBodyRef} style={{ height: `calc(100% - ${MONTH_HEADER_HEIGHT}px)` }}>
                {weeksToRender.map((week) => (
                    <div
                        key={week[0].toISOString()}
                        className="flex border-[1px] border-t-0 border-gray-300 last:rounded-b-lg"
                        style={{ height: `calc(100% / ${weeksToRender.length})` }}
                    >
                        {week.map((day) => (
                            <div
                                key={day.toISOString()}
                                className={`relative flex min-w-0 basis-full flex-col border-r-[1px] border-r-gray-300 p-1 last:border-r-0 ${!isDayInMonth(day, currentDate.month()) ? 'bg-[image:url("/diagonal_pattern.svg")] bg-cover' : 'bg-transparent'}`}
                            >
                                <CalendarDayBookables
                                    day={day}
                                    payees={payees}
                                    bookables={getBookables(day)}
                                    threshold={maxBookablesPerDay}
                                    lightenOverflowLabel={!isDayInMonth(day, currentDate.month())}
                                />
                                {!isToday(day) && (
                                    <Button
                                        shape="circle"
                                        size="small"
                                        intent="tertiary_deprecated"
                                        className="absolute right-1 bottom-1 size-6 no-underline"
                                        onClick={() => onGoToDay(day)}
                                    >
                                        <Label
                                            size="xs"
                                            className={`pointer-events-none ${!isDayInMonth(day, currentDate.month()) ? 'text-gray-400' : 'text-gray-700'}`}
                                        >
                                            {day.date()}
                                        </Label>
                                    </Button>
                                )}
                                {isToday(day) && (
                                    <Button
                                        shape="circle"
                                        size="small"
                                        intent="primary"
                                        className="absolute right-1 bottom-1 size-6"
                                        onClick={() => onGoToDay(day)}
                                    >
                                        <Label size="xs" className="pointer-events-none">
                                            {day.date()}
                                        </Label>
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </Card>
    )
}
